import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, ShoppingBag, User, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader,
} from "@/components/ui/sheet";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/shop", label: "Shop" },
  { to: "/quote", label: "Get a Quote" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_aquatics-quote-hub/artifacts/tk7jqsbi_copilot_image_1777416587330.jpeg";

function Logo({ logoUrl }) {
  const url = logoUrl || DEFAULT_LOGO;
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
      <div className="grid h-14 w-14 shrink-0 place-items-center md:h-16 md:w-16">
        <img src={url} alt="Fix It Aquatics, LLC" className="h-full w-full object-contain drop-shadow-sm" />
      </div>
      <div className="leading-tight">
        <div className="font-display text-base font-bold tracking-tight text-slate-900 md:text-lg">Fix It Aquatics, LLC</div>
        <div className="-mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-700">Veteran Owned & Operated</div>
      </div>
    </Link>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  useEffect(() => {
    api.get("/settings/public").then((r) => setLogoUrl(r.data.logo_url || null)).catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-40 border-b border-cyan-100/50 bg-white/75 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-8">
        <Logo logoUrl={logoUrl} />
        <nav className="hidden items-center gap-7 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-cyan-700" : "text-slate-700 hover:text-cyan-700"}`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="relative grid h-10 w-10 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 transition-colors hover:border-cyan-300 hover:text-cyan-700"
            data-testid="header-cart-button"
            aria-label="Open cart"
          >
            <ShoppingBag className="h-4 w-4" />
            {count > 0 && (
              <span className="absolute -right-1 -top-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-cyan-600 px-1 text-[11px] font-semibold text-white">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:border-cyan-300 md:inline-flex" data-testid="header-user-menu-trigger">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-cyan-100 text-cyan-800 text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() ?? "U"}
                  </span>
                  <span className="max-w-[120px] truncate">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-slate-500">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(user.role === "owner" || user.role === "admin" || user.role === "technician") ? (
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="header-dashboard-menu-item">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    {user.role === "owner" ? "Owner Dashboard" : user.role === "admin" ? "Admin Dashboard" : "My Jobs"}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => navigate("/account")} data-testid="header-my-account-menu-item">
                    <User className="mr-2 h-4 w-4" /> My account
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="header-logout-menu-item">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              className="hidden rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors hover:border-cyan-300 hover:text-cyan-700 md:inline-flex"
              data-testid="header-login-link"
            >
              <User className="mr-2 h-4 w-4" /> Sign in
            </Link>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 md:hidden" data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu className="h-4 w-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <SheetHeader><SheetTitle className="font-display">Menu</SheetTitle></SheetHeader>
              <div className="mt-4 flex flex-col gap-1">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={closeMobile}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-slate-800 hover:bg-cyan-50"
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="my-2 h-px bg-slate-200" />
                {user ? (
                  <>
                {(user.role === "owner" || user.role === "admin" || user.role === "technician") ? (
                  <NavLink to="/dashboard" onClick={closeMobile} className="rounded-lg px-3 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-50">
                    {user.role === "owner" ? "Owner Dashboard" : user.role === "admin" ? "Admin Dashboard" : "My Jobs"}
                  </NavLink>
                ) : (
                  <NavLink to="/account" onClick={closeMobile} className="rounded-lg px-3 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-50">
                    My account
                  </NavLink>
                )}
                    <button onClick={async () => { closeMobile(); await logout(); navigate("/"); }} className="rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-800 hover:bg-cyan-50">
                      Sign out
                    </button>
                  </>
                ) : (
                  <NavLink to="/login" onClick={closeMobile} className="rounded-lg px-3 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-50" data-testid="mobile-nav-signin-link">
                    Sign in
                  </NavLink>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
